'use client';

import type { SVGProps } from 'react';
import Image from 'next/image';
import Link from 'next/link';

import { useTranslations } from '@/components/language-provider';
import { useTheme } from '@/components/theme-provider';

const FOUNDING_APPLICATION_ENTRY = '/main-app/court-intelligence/founding-application';

function CheckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" {...props}>
      <path d="M4 10.5 8.5 15 16 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function FoundingVenuesMarketingPage({ hasSession }: { hasSession: boolean }) {
  const { messages } = useTranslations();
  const { actualTheme } = useTheme();
  const content = messages.foundingVenues;
  const courtIntelLogoSrc =
    actualTheme === 'dark' ? '/images/court-intelligence-logo-dark.svg' : '/images/court-intelligence-logo.svg';
  const signupHref = `/auth/signup?returnTo=${encodeURIComponent(FOUNDING_APPLICATION_ENTRY)}`;
  const loginHref = `/auth/login?returnTo=${encodeURIComponent(FOUNDING_APPLICATION_ENTRY)}`;
  const heroPrimaryHref = hasSession ? FOUNDING_APPLICATION_ENTRY : signupHref;
  const heroSecondaryHref = hasSession ? content.hero.secondaryHref : loginHref;
  const heroSecondaryLabel = hasSession ? content.hero.secondaryCta : messages.nav.login;
  const heroSecondaryExternal = hasSession;
  const finalPrimaryHref = hasSession ? FOUNDING_APPLICATION_ENTRY : signupHref;
  const finalSecondaryHref = hasSession ? content.finalCta.secondaryHref : loginHref;
  const finalSecondaryLabel = hasSession ? content.finalCta.secondaryCta : messages.nav.login;
  const finalSecondaryExternal = hasSession;

  return (
    <div className="space-y-16">
      <section className="grid gap-10 rounded-3xl border border-border/60 bg-surface/90 p-10 shadow-xl shadow-primary/10 backdrop-blur lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        <div className="space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            {content.hero.eyebrow}
          </span>
          <h1 className="text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            {content.hero.title}
          </h1>
          <p className="rounded-2xl bg-primary/10 px-4 py-3 text-sm font-semibold text-primary">
            {content.hero.highlight}
          </p>
          <p className="text-lg text-muted-foreground">{content.hero.description}</p>
          <p className="text-sm text-muted-foreground">{content.hero.note}</p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link
              href={heroPrimaryHref}
              className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold btn-primary shadow-lg shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {content.hero.primaryCta}
            </Link>
            <Link
              href={heroSecondaryHref}
              target={heroSecondaryExternal ? '_blank' : undefined}
              rel={heroSecondaryExternal ? 'noreferrer noopener' : undefined}
              className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {heroSecondaryLabel}
            </Link>
          </div>
          {!hasSession ? (
            <p className="text-sm text-muted-foreground">
              <Link
                href={content.hero.secondaryHref}
                target="_blank"
                rel="noreferrer noopener"
                className="font-medium text-primary transition hover:text-primary/80"
              >
                {content.hero.secondaryCta}
              </Link>
            </p>
          ) : null}
        </div>

        <div className="space-y-5 rounded-3xl border border-primary/25 bg-gradient-to-br from-primary/20 via-primary/10 to-background p-6 shadow-lg shadow-primary/10">
          <Image
            src={courtIntelLogoSrc}
            alt="Court Intelligence"
            width={1200}
            height={320}
            className="h-auto w-full max-w-[420px]"
          />
          <div className="space-y-3 rounded-2xl border border-border/60 bg-background/80 p-5">
            <h2 className="text-lg font-semibold text-foreground">{content.hero.visualTitle}</h2>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {content.hero.visualPoints.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground/80">{content.hero.visualFootnote}</p>
          </div>
        </div>
      </section>

      <section className="grid gap-6 rounded-3xl border border-border/60 bg-surface p-6 shadow-lg shadow-primary/5 sm:grid-cols-3 sm:p-8">
        {content.highlights.map((item) => (
          <div key={item.value} className="space-y-2 border-border/40 sm:border-r last:border-0 sm:pr-6 sm:last:pr-0">
            <p className="text-2xl font-bold text-foreground sm:text-3xl">{item.value}</p>
            <p className="text-sm text-muted-foreground sm:text-base">{item.label}</p>
          </div>
        ))}
      </section>

      <section className="space-y-8">
        <div className="space-y-3">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{content.benefits.title}</h2>
          <p className="max-w-3xl text-base text-muted-foreground sm:text-lg">{content.benefits.description}</p>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {content.benefits.items.map((item) => (
            <article
              key={item.title}
              className="flex h-full flex-col gap-4 rounded-2xl border border-border/60 bg-surface p-6 shadow-md shadow-primary/10"
            >
              <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {item.points.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="grid gap-8 lg:grid-cols-2">
        <article className="space-y-5 rounded-3xl border border-border/60 bg-surface p-8 shadow-lg shadow-primary/10">
          <div className="space-y-3">
            <h2 className="text-3xl font-bold tracking-tight text-foreground">{content.fit.title}</h2>
            <p className="text-base text-muted-foreground">{content.fit.description}</p>
          </div>
          <ul className="space-y-3 text-sm text-muted-foreground">
            {content.fit.items.map((item) => (
              <li key={item} className="flex items-start gap-3 rounded-2xl border border-border/50 bg-background px-4 py-3">
                <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </article>

        <article className="space-y-5 rounded-3xl border border-border/60 bg-surface p-8 shadow-lg shadow-primary/10">
          <h2 className="text-3xl font-bold tracking-tight text-foreground">{content.launchSupport.title}</h2>
          <div className="space-y-4">
            {content.launchSupport.steps.map((step, index) => (
              <div key={step.title} className="flex items-start gap-3 rounded-2xl border border-border/50 bg-background px-4 py-4">
                <span className="inline-flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
                  {index + 1}
                </span>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">{step.title}</p>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="space-y-6 rounded-3xl border border-border/60 bg-surface p-8 shadow-lg shadow-primary/10">
        <div className="space-y-3">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{content.checklist.title}</h2>
          <p className="max-w-3xl text-base text-muted-foreground sm:text-lg">{content.checklist.description}</p>
        </div>
        <ul className="grid gap-3 text-sm text-muted-foreground">
          {content.checklist.items.map((item) => (
            <li key={item} className="flex items-start gap-3 rounded-xl border border-border/50 bg-background px-4 py-3">
              <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground">
          {content.checklist.footnote}
        </p>
      </section>

      <section className="space-y-6 rounded-3xl border border-border/60 bg-surface px-6 py-10 text-center shadow-lg shadow-primary/10 sm:px-10">
        <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{content.finalCta.title}</h2>
        <p className="mx-auto max-w-3xl text-sm text-muted-foreground sm:text-base">{content.finalCta.description}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            href={finalPrimaryHref}
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold btn-primary shadow transition hover:-translate-y-0.5 hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {content.finalCta.primaryCta}
          </Link>
          <Link
            href={finalSecondaryHref}
            target={finalSecondaryExternal ? '_blank' : undefined}
            rel={finalSecondaryExternal ? 'noreferrer noopener' : undefined}
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {finalSecondaryLabel}
          </Link>
        </div>
        {!hasSession ? (
          <p className="text-sm text-muted-foreground">
            <Link
              href={content.finalCta.secondaryHref}
              target="_blank"
              rel="noreferrer noopener"
              className="font-medium text-primary transition hover:text-primary/80"
            >
              {content.finalCta.secondaryCta}
            </Link>
          </p>
        ) : null}
      </section>
    </div>
  );
}
